import { useEffect, useState, type FormEvent } from 'react';
import { Lock, PenLine, Trash2, UserPlus, UserRoundCheck, UserX } from 'lucide-react';
import { FolderCard } from '../ui/Vivid';
import type { TeamNote, TeamPresence, ThreadSummary } from '@mailpilot/shared';
import { useMe } from '../../hooks/useMe';
import { useCurrentWorkspace, useWorkspaceDetail } from '../../hooks/useWorkspace';
import { useAddNote, useAssignThread, useDeleteNote } from '../../hooks/useTeam';
import { fullDate, relativeTime } from '../../lib/format';
import { Avatar } from '../ui/Avatar';
import { Menu } from '../ui/Overlay';

/** Header control: who owns this conversation (organizations). */
export function AssignButton({ thread }: { thread: ThreadSummary }) {
  const me = useMe().data;
  const members = useWorkspaceDetail().data?.members ?? [];
  const assign = useAssignThread(thread.id);
  const a = thread.assignedTo;
  const mine = a && me && a.id === me.user.id;
  return (
    <Menu
      label="Assign conversation"
      align="right"
      width={260}
      header={<p className="px-3 pb-1 pt-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">Assign to</p>}
      trigger={(p) => (
        <button
          type="button"
          {...p}
          data-tip={a ? `Assigned to ${a.name || a.email}` : 'Assign'}
          aria-label={a ? `Assigned to ${a.name || a.email}. Change` : 'Assign this conversation'}
          className="tip flex h-9 items-center gap-1.5 rounded-xl px-2 text-[0.8125rem] font-medium text-muted transition-colors hover:bg-raised hover:text-ink"
        >
          {a ? <Avatar name={a.name} email={a.email} size={22} /> : <UserPlus className="h-[18px] w-[18px]" />}
          <span className="hidden lg:inline">{a ? (mine ? 'You' : (a.name || a.email).split(' ')[0]) : 'Assign'}</span>
        </button>
      )}
      items={[
        ...(me && !mine ? [{ key: 'me', label: 'Assign to me', icon: <UserRoundCheck className="h-4 w-4" />, onSelect: () => assign.mutate(me.user.id) }] : []),
        ...members
          .filter((m) => m.userId !== me?.user.id)
          .map((m) => ({
            key: m.userId,
            label: m.displayName,
            description: m.email,
            icon: <Avatar name={m.displayName} email={m.email} size={24} />,
            active: a?.id === m.userId,
            onSelect: () => assign.mutate(m.userId),
          })),
        ...(a ? ['divider' as const, { key: 'none', label: 'Unassign', icon: <UserX className="h-4 w-4" />, onSelect: () => assign.mutate(null) }] : []),
      ]}
    />
  );
}

/** "Ama is replying…" — refreshed by realtime presence events; entries expire on their own. */
export function ReplyingBanner({ replying }: { replying: TeamPresence[] }) {
  const meId = useMe().data?.user.id;
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 5000);
    return () => clearInterval(t);
  }, []);
  const others = replying.filter((r) => r.id !== meId && Date.now() - Date.parse(r.since) < 40_000);
  if (!others.length) return null;
  const names = others.map((r) => (r.name || r.email).split(' ')[0]);
  return (
    <div role="status" className="flex items-center gap-2 rounded-2xl bg-vivid-salmon px-4 py-2.5 text-[0.8125rem] font-semibold text-vivid-salmon-ink">
      <PenLine className="h-4 w-4 shrink-0 animate-pulse" aria-hidden />
      {names.join(', ')} {others.length === 1 ? 'is' : 'are'} replying to this conversation right now.
    </div>
  );
}

/** Private team notes, shown above the reply box. */
export function TeamNotes({ threadId, notes }: { threadId: string; notes: TeamNote[] }) {
  const me = useMe().data;
  const { isAdmin } = useCurrentWorkspace();
  const add = useAddNote(threadId);
  const del = useDeleteNote(threadId);
  const [text, setText] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (text.trim()) add.mutate(text.trim(), { onSuccess: () => setText('') });
  };
  return (
    <FolderCard as="section" tone="yellow" title="Team notes" icon={<Lock />} className="[&_section]:contents" bodyClassName="p-3.5" aria-label="Team notes">
      <p className="mp-folder-muted text-[0.6875rem] font-semibold uppercase tracking-wider">Only your team sees these</p>
      {notes.length > 0 && (
        <ul className="mt-2.5 space-y-2.5">
          {notes.map((n) => (
            <li key={n.id} className="group flex gap-2.5">
              <Avatar name={n.author?.name} email={n.author?.email ?? n.id} size={26} />
              <div className="min-w-0 flex-1">
                <p className="mp-folder-muted text-xs">
                  <span className="font-semibold text-current">{n.author?.name ?? 'Former member'}</span> · <span title={fullDate(n.createdAt)}>{relativeTime(n.createdAt)}</span>
                </p>
                <p className="mt-0.5 whitespace-pre-wrap break-words text-[0.8125rem]">{n.body}</p>
              </div>
              {(n.author?.id === me?.user.id || isAdmin) && (
                <button type="button" aria-label="Delete note" onClick={() => del.mutate(n.id)} className="mp-folder-muted self-start rounded-lg p-1 opacity-0 transition-opacity hover:bg-black/10 focus:opacity-100 group-hover:opacity-100">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={submit} className="mt-2.5 flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={4000}
          placeholder="Add a note for your team…"
          aria-label="Add a team note"
          className="h-9 min-w-0 flex-1 rounded-full border border-black/10 bg-white/70 px-3.5 text-[0.8125rem] text-vivid-yellow-ink placeholder:text-vivid-yellow-ink/50 focus:border-black/30 focus:outline-none dark:bg-white/80"
        />
        <button type="submit" disabled={!text.trim() || add.isPending} className="h-9 rounded-full bg-vivid-yellow-ink px-3.5 text-[0.8125rem] font-semibold text-vivid-yellow transition-opacity disabled:opacity-40">
          + Add note
        </button>
      </form>
    </FolderCard>
  );
}
